import * as React from 'react';
import {
    Alert, Box, Button, CircularProgress, MenuItem, Paper, Stack, Table, TableBody,
    TableCell, TableContainer, TableHead, TablePagination, TableRow, TextField, Typography
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import {MaterialTraceability as Api} from '../../controllers';
import {TokenValidator} from '../../utils';

const emptyFilters = {roll: '', orderId: '', caseId: '', machineId: '', client: '', source: '', dateFrom: '', dateTo: ''};
const displayTime = value => value ? new Date(value).toLocaleString() : '';

export default function Main() {
    const [machines, setMachines] = React.useState([]);
    const [filters, setFilters] = React.useState(emptyFilters);
    const [applied, setApplied] = React.useState(emptyFilters);
    const [result, setResult] = React.useState({rows: [], metrics: {totalRecords: 0, totalOrders: 0, totalPieces: 0}, pagination: {page: 1, limit: 25, pages: 1}});
    const [page, setPage] = React.useState(1);
    const [limit, setLimit] = React.useState(25);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState('');
    const [exporting, setExporting] = React.useState(false);

    const token = React.useCallback(() => TokenValidator.GetStaffUser(), []);

    const load = React.useCallback(async (activeFilters, activePage, activeLimit) => {
        setLoading(true);
        setError('');
        const response = await Api.History(await token(), {...activeFilters, page: activePage, limit: activeLimit});
        setLoading(false);
        if (!response || !response.success) {
            setError(response ? response.message : 'Unable to load material traceability history.');
            return;
        }
        setResult(response.result);
    }, [token]);

    React.useEffect(() => {
        const initialize = async () => {
            const staffToken = await token();
            const machineResponse = await Api.Machines(staffToken);
            if (machineResponse && machineResponse.success) setMachines(machineResponse.result);
            await load(emptyFilters, 1, 25);
        };
        initialize();
    }, [load, token]);

    const search = async event => {
        if (event) event.preventDefault();
        setApplied({...filters});
        setPage(1);
        await load(filters, 1, limit);
    };

    const changePage = async (_, zeroBasedPage) => {
        const next = zeroBasedPage + 1;
        setPage(next);
        await load(applied, next, limit);
    };

    const changeLimit = async event => {
        const next = Number(event.target.value);
        setLimit(next);
        setPage(1);
        await load(applied, 1, next);
    };

    const downloadCsv = async () => {
        setExporting(true);
        setError('');
        try {
            const response = await Api.HistoryCsv(await token(), applied);
            if (!response || !response.ok) {
                let message = 'Unable to export material traceability CSV.';
                try { const payload = await response.json(); if (payload.message) message = payload.message; } catch (_) {}
                setError(message);
                return;
            }
            const blob = await response.blob();
            const disposition = response.headers.get('content-disposition') || '';
            const match = disposition.match(/filename="?([^";]+)"?/i);
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = match ? match[1] : `material-traceability-${new Date().toISOString().replace(/[:.]/g, '-')}.csv`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(url);
        } finally {
            setExporting(false);
        }
    };

    return (
        <Box sx={{p: 3}}>
            <Stack direction={{xs: 'column', md: 'row'}} justifyContent='space-between' gap={2} sx={{mb: 2}}>
                <Box>
                    <Typography variant='h4'>Material Traceability</Typography>
                    <Typography color='text.secondary'>Track orders by material roll and machine</Typography>
                </Box>
                <Stack direction='row' gap={1}>
                    <Button startIcon={<DownloadIcon />} onClick={downloadCsv} disabled={loading || exporting}>Download CSV</Button>
                    <Button startIcon={<RefreshIcon />} onClick={() => load(applied, page, limit)} disabled={loading}>Refresh</Button>
                </Stack>
            </Stack>

            {error && <Alert severity='error' sx={{mb: 2}}>{error}</Alert>}

            <Paper component='form' onSubmit={search} sx={{p: 2, mb: 2}}>
                <Stack direction={{xs: 'column', md: 'row'}} gap={2} flexWrap='wrap'>
                    <TextField size='small' label='Roll Number' value={filters.roll} onChange={e => setFilters({...filters, roll: e.target.value})} />
                    <TextField size='small' label='Order ID' value={filters.orderId} onChange={e => setFilters({...filters, orderId: e.target.value})} />
                    <TextField size='small' label='Case ID' value={filters.caseId} onChange={e => setFilters({...filters, caseId: e.target.value})} />
                    <TextField select size='small' label='Machine' value={filters.machineId} sx={{minWidth: 180}}
                        onChange={e => setFilters({...filters, machineId: e.target.value})}>
                        <MenuItem value=''>All</MenuItem>
                        {machines.map(machine => <MenuItem key={machine.id} value={machine.id}>{machine.name}</MenuItem>)}
                    </TextField>
                    <TextField size='small' label='Client' value={filters.client} onChange={e => setFilters({...filters, client: e.target.value})} />
                    <TextField select size='small' label='Source' value={filters.source} sx={{minWidth: 140}}
                        onChange={e => setFilters({...filters, source: e.target.value})}>
                        <MenuItem value=''>All</MenuItem><MenuItem value='KLINE'>K Line</MenuItem><MenuItem value='CANDID'>Candid</MenuItem>
                    </TextField>
                    <TextField size='small' type='date' label='From' InputLabelProps={{shrink: true}} value={filters.dateFrom}
                        onChange={e => setFilters({...filters, dateFrom: e.target.value})} />
                    <TextField size='small' type='date' label='To' InputLabelProps={{shrink: true}} value={filters.dateTo}
                        onChange={e => setFilters({...filters, dateTo: e.target.value})} />
                    <Button type='submit' variant='contained' startIcon={<SearchIcon />} disabled={loading}>Search</Button>
                </Stack>
            </Paper>

            <Stack direction={{xs: 'column', md: 'row'}} gap={2} sx={{mb: 2}}>
                <Paper sx={{p: 2, flex: 1}}><Typography color='text.secondary'>Total Records</Typography><Typography variant='h5'>{result.metrics.totalRecords}</Typography></Paper>
                <Paper sx={{p: 2, flex: 1}}><Typography color='text.secondary'>Total Orders</Typography><Typography variant='h5'>{result.metrics.totalOrders}</Typography></Paper>
                <Paper sx={{p: 2, flex: 1}}><Typography color='text.secondary'>Total Pieces</Typography><Typography variant='h5'>{result.metrics.totalPieces}</Typography>
                    <Typography variant='caption'>Includes legitimate reprocessing runs.</Typography></Paper>
            </Stack>

            <TableContainer component={Paper}>
                <Table size='small'>
                    <TableHead><TableRow>
                        <TableCell>Date/Time</TableCell><TableCell>Machine</TableCell><TableCell>Roll</TableCell>
                        <TableCell>Order</TableCell><TableCell>Case ID</TableCell><TableCell>Client</TableCell><TableCell>Source</TableCell><TableCell align='right'>Pieces</TableCell>
                    </TableRow></TableHead>
                    <TableBody>
                        {loading && <TableRow><TableCell colSpan={8} align='center'><CircularProgress size={28} /></TableCell></TableRow>}
                        {!loading && result.rows.map(row => <TableRow key={row.id} hover>
                            <TableCell>{displayTime(row.registeredAt)}</TableCell><TableCell>{row.machine}</TableCell>
                            <TableCell>{row.rollNumber}</TableCell><TableCell>{row.orderId}</TableCell>
                            <TableCell>{row.caseId}</TableCell><TableCell>{row.client}</TableCell><TableCell>{row.source}</TableCell><TableCell align='right'>{row.pieces}</TableCell>
                        </TableRow>)}
                        {!loading && !result.rows.length && <TableRow><TableCell colSpan={8} align='center'>No matching traceability records.</TableCell></TableRow>}
                    </TableBody>
                </Table>
                <TablePagination component='div' count={result.metrics.totalRecords} page={page - 1}
                    onPageChange={changePage} rowsPerPage={limit} onRowsPerPageChange={changeLimit}
                    rowsPerPageOptions={[10, 25, 50, 100]} />
            </TableContainer>
        </Box>
    );
}
